import { useTranslation } from "react-i18next";
import React from "react";
import { Typography, Spin, Table } from "antd";
import { formatMoney } from "../../../helpers/numberHelper";
import FeesStatusBadge from "components/Common/Badge/FeesStatusBadge";
import { Link } from "react-router-dom";
import { all_routes } from "router/all_routes";
import Badge from "components/Common/ui/Badge";
import { formatToLocaleDate } from "utils/dateUtils";
import NoDataComponent from "components/Common/NoData";

const { Text } = Typography;

interface Props {
  tableData?: any[];
  loading?: boolean;
  filters?: {
    [key: string]: string | number | null;
  };
}

const MyFeesTable: React.FC<Props> = ({ tableData = [], loading = false }) => {
  const { t } = useTranslation();

  const expandedRowRender = (record: any) => {
    const paymentColumns = [
      {
        title: t("date"),
        dataIndex: "createdAt",
        key: "createdAt",
        render: (date: string | undefined) => formatToLocaleDate(date),

      },
      {
        title: t("amount"),
        dataIndex: "amount",
        key: "amount",
        render: (amount: number) => formatMoney(amount),
      },
      {
        title: t("payment_method"),
        dataIndex: ["paymentMethod", "name"],
        key: "paymentMethod",
      },
      {
        title: t("status"),
        dataIndex: "status",
        key: "status",
        render: (status: string) => (
          <Badge variant={status === "completed" ? "success" : "warning"} text={status} showIcon={false} />
        ),
      },
    ];

    return (
      <div className="p-3">
        <h6 className="mb-3">{t("payment_history")}</h6>
        <Table
          columns={paymentColumns}
          dataSource={record.feePayments || []}
          pagination={false}
          size="small"
          locale={{
            emptyText: <NoDataComponent />,
          }}
        />
      </div>
    );
  };

  const columns = [
    {
      title: t("type"),
      dataIndex: ["feeType", "name"],
      key: "feeType",
    },
    {
      title: t("due_date"),
      dataIndex: "dueDate",
      key: "dueDate",
      render: (dueDate: string | undefined) => formatToLocaleDate(dueDate),
    },
    {
      title: t("amount"),
      dataIndex: "amount",
      key: "amount",
      render: (amount: string) => formatMoney(parseFloat(amount)),
    },
    {
      title: t("remaining"),
      key: "remaining",
      render: (record: any) => formatMoney(record.amountNotPaid ?? 0),
    },
    {
      title: t("status"),
      dataIndex: "computedStatus",
      key: "computedStatus",
      render: (computedStatus: string) => (
        <FeesStatusBadge
          status={computedStatus as "paid" | "partially_paid" | "pending" | "failed" | "overdue" | "coming"}
        />
      ),
    },
    {
      title: t("action"),
      key: "action",
      render: (_: any, record: any) => {
        console.log(record);
        return record.status === "paid" ? (
          <Link
            to={`${all_routes.showFee.replace(":feeId", record.id)}`}
            className="btn btn-primary btn-sm"
          >
            <i className="ti ti-download me-2"></i>
            {t("download")}
          </Link>
        ) : null;
      },
    },
  ];

  if (loading) {
    return (
      <div className="d-flex justify-content-center my-5">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Table
      columns={columns}
      dataSource={tableData}
      expandable={{
        expandedRowRender,
        expandIcon: ({ expanded, onExpand, record }) => (
          <button
            className="btn btn-sm btn-link"
            onClick={(e) => onExpand(record, e)}
          >
            <i className={`ti ti-chevron-${expanded ? "up" : "down"}`}></i>
          </button>
        ),
      }}
      locale={{
        emptyText: <NoDataComponent />,
      }}
      rowKey="id"
      pagination={false}
    />
  );
};

export default MyFeesTable;
